import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fragment, forwardRef, useEffect, useRef } from 'react';
import { Box, Typography, FormControlLabel, Checkbox, TextField, InputAdornment } from '@mui/material';
import CustomSelect2 from './CustomSelect2';
import SelectPlanCheckbox from './SelectPlanCheckbox';
import PercentInput from './PercentInput';
import NotesDisplay from './NotesDisplay';
import RatesVersionSelect from './RatesVersionSelect';
import CopyImageButton from './CopyImageButton';
import CopyExcelDataButton from './CopyExcelDataButton';
import TableCellRate from './TableCellRate';
import TableCellText from './TableCellText';

// Maps a room type label to the slug the rate provider uses to build its response keys,
// e.g. tier 'plus' + room type 'Semi-Private' -> 'select_plus_semi_private_with_travel_plus'.
const ROOM_TYPE_SLUGS = {
  'Ward': 'ward',
  'Semi-Private': 'semi_private',
  'Private 2M': 'private_2m',
  'Private 3M': 'private_3m',
  'Private 5M': 'private_5m'
};

function getRateField(tier, roomType) {
  return `select_${tier}_${ROOM_TYPE_SLUGS[roomType]}_with_travel_plus`;
}

const PacificCrossSelect_Rates = forwardRef(({ appConfig, sx, clients, loadingStatus, currency, selectedPlans, setSelectedPlans,
  response, addToPendingTasks,
  discounts, setDiscounts, loadings, setLoadings, computedRates, setComputedRates,
  specificAreaOfCoverage, setSpecificAreaOfCoverage, areaOfCoverage,
  setShowImageCopy, selectedOptions, addSelectedOption,
  setViewSelectedOption, specificCurrency, setSpecificCurrency,
  specificPaymentFrequency, setSpecificPaymentFrequency,
  pacificCrossSelectSpecific, setPacificCrossSelectSpecific,
  insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, setShowRefreshNow }, ref) => {

  const id = 'pacific_cross_select';

  const containerRef = useRef(null);

  const config = appConfig?.content?.specifics?.pacific_cross_select || {};

  const outpatient_rates = config?.['Outpatient Rates'] || {};
  const dental_rates = config?.['Dental Rates'] || {};
  const co_payment_rates = config?.['Co-Payment Rates'] || {};
  const personal_accident_coverage_rates = config?.['Personal Accident Coverage Rates'] || {};
  const group_discounts = config?.['Group Discounts'] || {};
  const payment_frequency_charges = config?.['Payment Frequency Surcharge'] || {};

  // Outpatient/Client Type option labels come from the config's nested keys, so a rate
  // sheet update (e.g. a new outpatient tier) shows up here without a code change.
  const outpatientBands = Object.keys(outpatient_rates);
  const outpatientBenefitItems = ['No', ...(outpatientBands.length > 0 ? Object.keys(outpatient_rates?.[outpatientBands[0]] || {}) : ['Standard', 'Executive'])];

  const dentalBands = Object.keys(dental_rates);
  const clientTypeItems = dentalBands.length > 0 ? Object.keys(dental_rates?.[dentalBands[0]] || {}) : ['Individual', 'Group'];

  const coPaymentItems = Object.keys(co_payment_rates).length > 0 ? Object.keys(co_payment_rates) : ['Nil'];

  const personalAccidentCoverageItems = ['No', ...Object.keys(personal_accident_coverage_rates)];

  // What TableCellRate prints as a dash - shown until the rates are fetched.
  const NO_RATE = '-';

  // Each tier is one whole plan - the room type only decides which of the rate provider's
  // pre-computed columns is shown/summed for that tier, it isn't sent back to the server.
  const tiers = [
    { tierId: 'plus', planId: 'pacific_cross_select_plus', label: 'Select Plus', roomTypes: ['Ward', 'Semi-Private', 'Private 2M', 'Private 3M', 'Private 5M'] },
    { tierId: 'standard', planId: 'pacific_cross_select_standard', label: 'Select Standard', roomTypes: ['Ward', 'Semi-Private', 'Private 2M'] }
  ];

  useEffect(() => {
    computedRates[id] = getComputedRates();
    setComputedRates(computedRates);
  }, [response, discounts[id], loadings[id], pacificCrossSelectSpecific, clients.length]);

  useEffect(() => {
    setTimeout(() => {
      addSelectedOption(id, 'client_type', pacificCrossSelectSpecific['client_type']);
      addSelectedOption(id, 'co_payment', pacificCrossSelectSpecific['co_payment']);
      addSelectedOption(id, 'personal_accident_coverage', pacificCrossSelectSpecific['personal_accident_coverage']);
      addSelectedOption(id, 'outpatient_benefit', pacificCrossSelectSpecific['outpatient_benefit']);
      addSelectedOption(id, 'dental_benefit', pacificCrossSelectSpecific['dental_benefit']);
      addSelectedOption(id, 'plus_room_type', pacificCrossSelectSpecific['plus_room_type']);
      addSelectedOption(id, 'standard_room_type', pacificCrossSelectSpecific['standard_room_type']);
      addSelectedOption(id, 'discount', discounts[id] == '' ? 0 : discounts[id]);
      addSelectedOption(id, 'loading', loadings[id] == '' ? 0 : loadings[id]);
    }, 1000);
  }, [pacificCrossSelectSpecific, discounts[id], loadings[id]]);

  useEffect(() => {
    setViewSelectedOption(id, 'client_type', (option_value) => {
      pacificCrossSelectSpecific['client_type'] = option_value;
      setPacificCrossSelectSpecific({ ...pacificCrossSelectSpecific });
    });

    setViewSelectedOption(id, 'co_payment', (option_value) => {
      pacificCrossSelectSpecific['co_payment'] = option_value;
      setPacificCrossSelectSpecific({ ...pacificCrossSelectSpecific });
    });

    setViewSelectedOption(id, 'personal_accident_coverage', (option_value) => {
      pacificCrossSelectSpecific['personal_accident_coverage'] = option_value;
      setPacificCrossSelectSpecific({ ...pacificCrossSelectSpecific });
    });

    setViewSelectedOption(id, 'outpatient_benefit', (option_value) => {
      pacificCrossSelectSpecific['outpatient_benefit'] = option_value;
      setPacificCrossSelectSpecific({ ...pacificCrossSelectSpecific });
    });

    setViewSelectedOption(id, 'dental_benefit', (option_value) => {
      pacificCrossSelectSpecific['dental_benefit'] = option_value;
      setPacificCrossSelectSpecific({ ...pacificCrossSelectSpecific });
    });

    setViewSelectedOption(id, 'plus_room_type', (option_value) => {
      pacificCrossSelectSpecific['plus_room_type'] = option_value;
      setPacificCrossSelectSpecific({ ...pacificCrossSelectSpecific });
    });

    setViewSelectedOption(id, 'standard_room_type', (option_value) => {
      pacificCrossSelectSpecific['standard_room_type'] = option_value;
      setPacificCrossSelectSpecific({ ...pacificCrossSelectSpecific });
    });

    setViewSelectedOption(id, 'discount', (option_value) => {
      discounts[id] = option_value;
      setDiscounts(discounts);
    });

    setViewSelectedOption(id, 'loading', (option_value) => {
      loadings[id] = option_value;
      setLoadings(loadings);
    });
  }, [selectedOptions]);

  // Ticks Select Plus the first time this card is shown - once a tier has been ticked
  // (here or restored from a saved quotation) this never overrides it again.
  useEffect(() => {
    if (getSelectedTiers().length === 0) {
      setSelectedPlans([...selectedPlans, tiers[0].planId]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One entry per tier, so the Premiums Summary can compare this insurer's plans against
  // the other insurers' - each entry is what the client actually pays for that tier.
  function getComputedRates() {
    if (!response) return;

    const rates = response.rates;

    const _computedRates = [];

    for (let i = 0; i < rates.length; i++) {
      const computedRate = {
        'client': rates[i].client,
        'currency': rates[i].currency
      };

      tiers.forEach(({ tierId }) => {
        computedRate[tierId] = getFinalPremium(i, tierId);
      });

      _computedRates.push(computedRate);
    }

    return _computedRates;
  }

  function getRate(clientIndex, benefit) {
    if (!response) return NO_RATE;

    if (!response.rates[clientIndex]) return NO_RATE;

    const rate = response.rates[clientIndex][benefit];

    if (rate === undefined || rate === null) return NO_RATE;

    return rate;
  }

  function getTierRoomType(tierId) {
    return pacificCrossSelectSpecific[`${tierId}_room_type`] || 'Ward';
  }

  function getTierRate(clientIndex, tierId) {
    return getRate(clientIndex, getRateField(tierId, getTierRoomType(tierId)));
  }

  // 'Unavailable' (or any other note the rate sheet carries) has to survive every sum
  // untouched, so a premium built on top of it is never shown as a number.
  function isRateAvailable(rate) {
    return !(rate === '' || rate === null || rate === undefined || typeof rate === 'string' || isNaN(rate));
  }

  function getSelectedTiers() {
    return tiers.filter(({ planId }) => selectedPlans.includes(planId));
  }

  // The tier premium before any discount - the sum of every plan tier ticked above.
  function getPremium(clientIndex) {
    const _selectedTiers = getSelectedTiers();

    if (_selectedTiers.length === 0) return 0;

    let total = 0;

    for (let i = 0; i < _selectedTiers.length; i++) {
      const rate = getTierRate(clientIndex, _selectedTiers[i].tierId);

      if (!isRateAvailable(rate)) return rate;

      total += rate;
    }

    return total;
  }

  function isGroupClientType() {
    return (pacificCrossSelectSpecific['client_type'] || clientTypeItems[0]) === 'Group';
  }

  // Group Discounts only make sense for the Group client type - Individual quotes never
  // get a volume discount no matter how many clients are on it.
  function getGroupDiscountRate() {
    if (!isGroupClientType()) return 0;

    const clients_count = clients.length;

    if (clients_count === 0) return 0;

    let rate = 0;

    Object.keys(group_discounts).forEach(bracket => {
      if (bracket.endsWith('+')) {
        const min = parseInt(bracket, 10);

        if (!isNaN(min) && clients_count >= min) {
          rate = group_discounts[bracket];
        }
      } else if (bracket.includes('-')) {
        const [min, max] = bracket.split('-').map(part => parseInt(part.trim(), 10));

        if (clients_count >= min && clients_count <= max) {
          rate = group_discounts[bracket];
        }
      } else if (clients_count === parseInt(bracket, 10)) {
        rate = group_discounts[bracket];
      }
    });

    return rate;
  }

  function getRenewalPercent() {
    const discount = parseFloat(discounts[id]) || 0;
    const loading = parseFloat(loadings[id]) || 0;

    return (100 - discount + loading) * 0.01;
  }

  // Group Discount + Renewal Discount/Loading combined into one multiplier, applied to
  // the base rate and to Outpatient - Dental and Personal Accident Coverage stay flat.
  function getDiscountFactor() {
    return (1 - getGroupDiscountRate()) * getRenewalPercent();
  }

  function getPremiumAfterDiscount(clientIndex, tierId = null) {
    const premium = tierId ? getTierRate(clientIndex, tierId) : getPremium(clientIndex);

    if (!isRateAvailable(premium)) return premium;

    return premium * getDiscountFactor();
  }

  function getOutpatientBenefit() {
    return pacificCrossSelectSpecific['outpatient_benefit'] || outpatientBenefitItems[0] || 'Standard';
  }

  function getOptionalRate(clientIndex, benefit) {
    if (benefit === 'outpatient') {
      if (getOutpatientBenefit() === 'No') return 0;

      const outpatientField = getOutpatientBenefit() === 'Executive' ? 'op_executive' : 'op_standard';

      return getRate(clientIndex, outpatientField);
    }

    if (benefit === 'dental') {
      if (pacificCrossSelectSpecific['dental_benefit'] !== 'Yes') return 0;

      return getRate(clientIndex, 'dental');
    }

    if (benefit === 'personal_accident_coverage') {
      if (!pacificCrossSelectSpecific['personal_accident_coverage'] || pacificCrossSelectSpecific['personal_accident_coverage'] === 'No') return 0;

      return getRate(clientIndex, 'personal_accident_coverage_rate');
    }

    return 0;
  }

  // Outpatient rides the same Group/Renewal discount as the base rate - Dental and
  // Personal Accident Coverage are flat add-ons that sit outside it either way.
  function getDiscountedOptionalRate(clientIndex, benefit) {
    const rate = getOptionalRate(clientIndex, benefit);

    if (!isRateAvailable(rate)) return rate;

    if (benefit === 'outpatient') {
      return rate * getDiscountFactor();
    }

    return rate;
  }

  function getOptionalPremium(clientIndex) {
    let total = 0;

    ['outpatient', 'dental', 'personal_accident_coverage'].forEach(benefit => {
      const rate = getDiscountedOptionalRate(clientIndex, benefit);

      if (isRateAvailable(rate)) {
        total += rate;
      }
    });

    return total;
  }

  function getFinalPremium(clientIndex, tierId = null) {
    const premium = getPremiumAfterDiscount(clientIndex, tierId);

    if (!isRateAvailable(premium)) return premium;

    return premium + getOptionalPremium(clientIndex);
  }

  function getSubtotal(getRateForClient) {
    let subTotal = 0;

    for (let i = 0; i < clients.length; i++) {
      const rate = getRateForClient(i);

      if (!isRateAvailable(rate)) return rate;

      subTotal += rate;
    }

    return subTotal;
  }

  // Only Annually is quoted directly by the rate sheet, other frequencies are derived
  // here from the surcharges the rate sheet's config carries.
  function getPremiumPerPaymentFrequency(payment_frequency, clientIndex = null, tierId = null) {
    const annual = clientIndex === null
      ? getSubtotal(idx => getFinalPremium(idx, tierId))
      : getFinalPremium(clientIndex, tierId);

    if (!isRateAvailable(annual)) return annual;

    const surcharge = payment_frequency_charges[payment_frequency] || 0;

    if (payment_frequency === 'Semi Annually') {
      return (annual + (annual * surcharge)) / 2;
    }

    const installments = {
      'Annually': 1,
      'Quarterly': 4,
      'Monthly': 12
    }[payment_frequency] || 1;

    return (annual + (annual * surcharge)) / installments;
  }

  // Matches the rate sheet's labeling for the Semi Annually row specifically -
  // other payment frequencies keep their default "+X%" label.
  function getPaymentFrequencyLabel(payment_frequency) {
    const percent = ((payment_frequency_charges[payment_frequency] || 0) * 100).toFixed(0);

    if (payment_frequency === 'Semi Annually') {
      return `Semi Annual Premium (${percent}%)`;
    }

    return `${payment_frequency} Premium (+${percent}%)`;
  }

  function getCurrency() {
    if (!response) return specificCurrency[id];

    if (!response.rates || response.rates.length === 0) return specificCurrency[id];

    return response.rates[0].currency;
  }

  function getGroupDiscountLabel() {
    return `${(getGroupDiscountRate() * 100).toFixed(0)}%`;
  }

  function handleSpecificChange(key, value) {
    pacificCrossSelectSpecific[key] = value;
    setPacificCrossSelectSpecific({ ...pacificCrossSelectSpecific });
  }

  // client_type, co_payment and personal_accident_coverage feed the rate provider directly,
  // so changing them needs a re-fetch. Room type / outpatient / dental only pick which
  // already-fetched value is shown, so they don't.
  function handleFetchedSpecificChange(key, value) {
    handleSpecificChange(key, value);

    addToPendingTasks([id]);
  }

  const optionalRows = [
    { key: 'outpatient', label: `Outpatient (${getOutpatientBenefit()})`, visible: getOutpatientBenefit() !== 'No' },
    { key: 'dental', label: 'Dental', visible: pacificCrossSelectSpecific['dental_benefit'] === 'Yes' },
    { key: 'personal_accident_coverage', label: 'Personal Accident Coverage', visible: !!pacificCrossSelectSpecific['personal_accident_coverage'] && pacificCrossSelectSpecific['personal_accident_coverage'] !== 'No' }
  ].filter(row => row.visible);

  return (
    <Box id={id} sx={sx}>
      <Paper sx={{ borderColor: '#e0e0e0', borderWidth: 2, pb: 2, scrollMarginTop: '200px' }} variant='outlined' ref={containerRef}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1565c0', px: 2, py: 1 }}>
          <div style={{
            color: '#fff',
            backgroundColor: '#1565c0',
            padding: 6,
            fontFamily: 'Poppins, sans-serif',
            fontSize: 18,
            lineHeight: 1.2,
            height: 60
          }}>Pacific Cross Select</div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, background: '#f8f9fa', px: 4 }}>
            <img
              src={`${process.env.PUBLIC_URL}/images/pacific-cross-logo.avif`}
              alt="Pacific Cross"
              style={{ height: 60, userSelect: 'none', WebkitUserDrag: 'none', transform: 'scale(1.5)' }}
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
          </Box>
        </Box>
        <Box sx={{ p: 2 }}>

          <Box sx={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', gap: 5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography fontSize={15} fontWeight={600}>Plan Type</Typography>
                  {
                    tiers.map(({ planId, label }) => (
                      <Box key={planId} sx={{ height: 32, mb: 0.5, display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                          sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                          control={<SelectPlanCheckbox size="small" selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId={planId} />}
                          label={label}
                        />
                      </Box>
                    ))
                  }
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography fontSize={15} fontWeight={600}>Core Benefits</Typography>
                  {
                    tiers.map(({ tierId, planId, roomTypes }) => (
                      <Box key={planId} sx={{ height: 32, mb: 0.5, display: 'flex', alignItems: 'center' }}>
                        <CustomSelect2
                          dense
                          sx={{ width: 160 }}
                          value={getTierRoomType(tierId)}
                          onChange={(e) => handleSpecificChange(`${tierId}_room_type`, e.target.value)}
                          items={roomTypes}
                          disabled={loadingStatus[id]}
                          isZeroDeductibleUniform={false}
                        />
                      </Box>
                    ))
                  }
                </Box>
              </Box>

              <Box>
                <Typography fontSize={15} fontWeight={600}>Client Type</Typography>
                {
                  clientTypeItems.map(client_type => (
                    <FormControlLabel
                      key={client_type}
                      sx={{ mb: 0, display: 'block', '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                      control={
                        <Checkbox
                          size="small"
                          checked={(pacificCrossSelectSpecific['client_type'] || clientTypeItems[0]) === client_type}
                          onChange={() => handleFetchedSpecificChange('client_type', client_type)}
                          disabled={loadingStatus[id]}
                        />
                      }
                      label={client_type}
                    />
                  ))
                }
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Co-Payment</Typography>
                <CustomSelect2
                  dense
                  sx={{ width: 140 }}
                  value={pacificCrossSelectSpecific['co_payment'] || coPaymentItems[0] || ''}
                  onChange={(e) => handleFetchedSpecificChange('co_payment', e.target.value)}
                  items={coPaymentItems}
                  disabled={loadingStatus[id]}
                  isZeroDeductibleUniform={false}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Outpatient Benefits</Typography>
                <CustomSelect2
                  dense
                  sx={{ width: 140 }}
                  value={getOutpatientBenefit()}
                  onChange={(e) => handleSpecificChange('outpatient_benefit', e.target.value)}
                  items={outpatientBenefitItems}
                  disabled={loadingStatus[id]}
                  isZeroDeductibleUniform={false}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Dental Benefits</Typography>
                <CustomSelect2
                  dense
                  sx={{ width: 140 }}
                  value={pacificCrossSelectSpecific['dental_benefit'] || 'No'}
                  onChange={(e) => handleSpecificChange('dental_benefit', e.target.value)}
                  items={['No', 'Yes']}
                  disabled={loadingStatus[id]}
                  isZeroDeductibleUniform={false}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Personal Accident Coverage</Typography>
                <CustomSelect2
                  dense
                  sx={{ width: 140 }}
                  value={pacificCrossSelectSpecific['personal_accident_coverage'] || 'No'}
                  onChange={(e) => handleFetchedSpecificChange('personal_accident_coverage', e.target.value)}
                  items={personalAccidentCoverageItems}
                  disabled={loadingStatus[id]}
                  isZeroDeductibleUniform={false}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Renewal/Further Discount</Typography>
                <PercentInput
                  value={discounts[id] ?? 0}
                  onCommit={(value) => setDiscounts(prev => ({ ...prev, [id]: value }))}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Renewal/Further Loading</Typography>
                <PercentInput
                  value={loadings[id] ?? 0}
                  onCommit={(value) => setLoadings(prev => ({ ...prev, [id]: value }))}
                />
              </Box>
            </Box>
          </Box>

          {
            isGroupClientType() &&
            <Box sx={{ display: 'flex', gap: 5, mt: 2, flexWrap: 'wrap' }}>
              <Typography fontSize={13}>
                Group Discount: <span style={{ fontSize: 13, color: '#1565c0', fontWeight: 600 }}>{getGroupDiscountLabel()}</span>
              </Typography>
            </Box>
          }

          <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'end', width: '100%', marginBottom: 7, marginTop: 24 }}>
            <CopyExcelDataButton
              elementId='table-pacific-cross-select-rates'
              style={{ marginRight: 7, fontSize: 11, padding: '2px 8px', minHeight: 0 }}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
            />
            <CopyImageButton
              elementId='table-pacific-cross-select-rates'
              setShowImageCopy={setShowImageCopy}
              style={{ fontSize: 11, padding: '2px 8px', minHeight: 0 }}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
            />
          </div>

          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table id='table-pacific-cross-select-rates' size='small' style={{ minWidth: 650, background: '#fff', width: `calc(100% + ${clients.length > 5 ? clients.length - 5 : 0} * 75px)` }}>
              <TableHead>
                <TableRow style={{ background: '#1565c0' }}>
                  <TableCell style={{ width: 260, color: '#fff', fontWeight: 600 }}>Plan</TableCell>
                  {
                    clients.map((client, idx) => {
                      if (client.full_name.trim() === '') {
                        return <TableCell style={{ width: 200, fontWeight: 600, color: '#fff' }} key={idx}>_</TableCell>
                      }

                      return <TableCell style={{ width: 200, color: '#fff', fontWeight: 600 }} key={idx}>
                        <div>{client.full_name} ({client.age})</div>
                        <div>{client.country_of_residence}</div>
                      </TableCell>
                    })
                  }
                  <TableCell style={{ color: '#fff', fontWeight: 600 }}>Subtotal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {
                  tiers.map(({ tierId, planId, label }) => {
                    if (!selectedPlans.includes(planId)) return null;

                    return (
                      <TableRow key={planId}>
                        <TableCellText content={`${label} (${getTierRoomType(tierId)})`} style={{ width: 260, minWidth: 260 }} />
                        {
                          clients.map((client, idx) => (
                            <TableCellRate
                              key={client.uid}
                              rate={getTierRate(idx, tierId)}
                              currency={getCurrency()}
                              isLoading={loadingStatus[id]}
                            />
                          ))
                        }
                        <TableCellRate
                          rate={getSubtotal(clientIndex => getTierRate(clientIndex, tierId))}
                          currency={getCurrency()}
                          isLoading={loadingStatus[id]}
                        />
                      </TableRow>
                    );
                  })
                }

                {
                  getGroupDiscountRate() > 0 &&
                  getSelectedTiers().map(({ tierId, planId, label }) => (
                    <TableRow key={planId}>
                      <TableCellText content={`${label} Premium after discount`} style={{ fontWeight: 600 }} bgColor='#ffcc80' />
                      {
                        clients.map((client, idx) => (
                          <TableCellRate
                            key={client.uid}
                            rate={getPremiumAfterDiscount(idx, tierId)}
                            currency={getCurrency()}
                            isLoading={loadingStatus[id]}
                            bgColor='#ffcc80'
                          />
                        ))
                      }
                      <TableCellRate
                        rate={getSubtotal(clientIndex => getPremiumAfterDiscount(clientIndex, tierId))}
                        currency={getCurrency()}
                        isLoading={loadingStatus[id]}
                        bgColor='#ffcc80'
                      />
                    </TableRow>
                  ))
                }

                {
                  optionalRows.length > 0 &&
                  <TableRow>
                    <TableCell colSpan={clients.length + 2} style={{ fontWeight: 600, background: '#1565c0', color: '#fff' }}>Optional Premium</TableCell>
                  </TableRow>
                }

                {
                  optionalRows.map(({ key, label }) => (
                    <TableRow key={key}>
                      <TableCellText content={label} style={{ width: 260, minWidth: 260 }} />
                      {
                        clients.map((client, idx) => (
                          <TableCellRate
                            key={client.uid}
                            rate={getDiscountedOptionalRate(idx, key)}
                            currency={getCurrency()}
                            isLoading={loadingStatus[id]}
                          />
                        ))
                      }
                      <TableCellRate
                        rate={getSubtotal(clientIndex => getDiscountedOptionalRate(clientIndex, key))}
                        currency={getCurrency()}
                        isLoading={loadingStatus[id]}
                      />
                    </TableRow>
                  ))
                }

                {/* One Annual + payment frequency block per ticked tier, separated by a blank row. */}
                {
                  getSelectedTiers().map(({ tierId, planId, label }, tierIndex) => (
                    <Fragment key={planId}>
                      {
                        tierIndex > 0 &&
                        <TableRow>
                          <TableCell colSpan={clients.length + 2} style={{ height: 20, padding: 0, background: '#fff' }} />
                        </TableRow>
                      }

                      <TableRow>
                        <TableCellText content={`${label} Annual Premium`} style={{ fontWeight: 600 }} bgColor='#ffcc80' />
                        {
                          clients.map((client, idx) => (
                            <TableCellRate
                              key={client.uid}
                              rate={getFinalPremium(idx, tierId)}
                              currency={getCurrency()}
                              isLoading={loadingStatus[id]}
                              bgColor='#ffcc80'
                            />
                          ))
                        }
                        <TableCellRate
                          rate={getSubtotal(clientIndex => getFinalPremium(clientIndex, tierId))}
                          currency={getCurrency()}
                          isLoading={loadingStatus[id]}
                          bgColor='#ffcc80'
                        />
                      </TableRow>

                      {
                        Object.keys(payment_frequency_charges).filter(payment_frequency => payment_frequency !== 'Annually').map(payment_frequency => (
                          <TableRow key={payment_frequency}>
                            <TableCellText
                              content={`${label} ${getPaymentFrequencyLabel(payment_frequency)}`}
                              style={{ fontWeight: 600 }}
                              bgColor='#ffcc80'
                            />
                            {
                              clients.map((client, idx) => (
                                <TableCellRate
                                  key={client.uid}
                                  rate={getPremiumPerPaymentFrequency(payment_frequency, idx, tierId)}
                                  currency={getCurrency()}
                                  isLoading={loadingStatus[id]}
                                  bgColor='#ffcc80'
                                />
                              ))
                            }
                            <TableCellRate
                              rate={getPremiumPerPaymentFrequency(payment_frequency, null, tierId)}
                              currency={getCurrency()}
                              isLoading={loadingStatus[id]}
                              bgColor='#ffcc80'
                            />
                          </TableRow>
                        ))
                      }
                    </Fragment>
                  ))
                }
              </TableBody>
            </Table>
          </TableContainer>

          <NotesDisplay appConfig={appConfig} insurerId={id} />

          <RatesVersionSelect
            insurer={id}
            label="Rates Version"
            insurerRates={insurerRates}
            selectedRatesVersion={selectedRatesVersion}
            setSelectedRatesVersion={setSelectedRatesVersion}
            disabled={loadingStatus[id]}
            handleFetchAppConfig={handleFetchAppConfig}
            onChange={() => setShowRefreshNow(true)}
          />
        </Box>
      </Paper>
    </Box>
  );
});

export default PacificCrossSelect_Rates;
